/**
 *  @class
 *  @function Pagination
 */

if (!customElements.get('pagination-theme')) {
  class Pagination extends HTMLElement {
    constructor() {
      super();
      const id = this.dataset.section;
      this.button = this.querySelector('.load-more');
      this.grid = document.querySelector('[data-id=' + id + ']');
      this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined';
      // Next page to fetch. Landing on ?page=3 must continue with 4, not reload 2.
      this.i = (parseInt(this.dataset.page, 10) || 1) + 1;
      this.firstPage = this.i - 1;
      this.pages = parseInt(this.dataset.pages, 10) || 0;
    }
    connectedCallback() {
      if (this.classList.contains('pagination-type--loadmore')) {
        this.loadMore();
        this.showEarlierPages();
      }
      if (this.classList.contains('pagination-type--infinite')) {
        this.infinite();
      }
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      this.clearError();
    }
    addUrlParam(search, key) {
      var newParam = key + '=' + this.i,
        params = '?' + newParam;

      // If the "search" string exists, then build params from it
      if (search) {
        // Try to replace an existance instance
        params = search.replace(new RegExp('([?&])' + key + '[^&]*'), '$1' + newParam);

        // If nothing was replaced, then add the new param to the end
        if (params === search) {
          params += '&' + newParam;
        }
      }

      return params;
    }
    loadMore() {
      let base = this;
      if (!this.button) return;
      this.button.addEventListener('click', function (e) {
        // A real link for crawlers and no-JS visitors; here it loads in place instead.
        e.preventDefault();
        base.loadProducts();
        this.blur();
      });
    }
    infinite() {
      let base = this;
      base.observer = new IntersectionObserver(function (entries) {
        if (entries[0].intersectionRatio === 1) {
          base.loadProducts();
        }
      }, {
        threshold: [0, 1]
      });
      base.observer.observe(base);
    }
    loadProducts() {
      let base = this,
        url = document.location.pathname + base.addUrlParam(document.location.search, 'page');
      if (base.getAttribute('loading')) {
        return;
      }
      base.setAttribute('loading', true);
      base.clearError();
      base.fetchPage(url)
        .then((html) => {
          base.i++;
          base.renderProducts(html, url);

          dispatchCustomEvent('pagination:page-changed', {
            url: url
          });
        })
        // A failed request must leave the button working, and say so, or the rest of the collection is out of reach.
        .catch(() => {
          base.removeAttribute('loading');
          base.showError(base.button);
        });
    }
    // Only this section, not the whole page. An error page has no grid, so it counts as a failure.
    fetchPage(url) {
      return fetch(url + (url.includes('?') ? '&' : '?') + 'section_id=' + this.dataset.section)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((html) => {
          if (!html.includes('id="product-grid"')) throw new Error('no grid');
          return html;
        });
    }
    showError(after) {
      if (!after) return;
      const note = document.createElement('p');
      note.className = 'form-notification error load-more__error';
      note.setAttribute('role', 'alert');
      note.textContent = this.dataset.error;
      after.after(note);
    }
    clearError() {
      document.querySelectorAll('.load-more__error').forEach((note) => note.remove());
    }
    // BUCKS only converts what it saw at load: new cards would stay in the shop's currency next to converted ones.
    convertPrices() {
      if (window.bucksCC && typeof window.bucksCC.reConvert === 'function') window.bucksCC.reConvert();
    }
    // Opened on a later page (a reload, or back from a product): count what is really here and offer the earlier pages.
    showEarlierPages() {
      if (this.firstPage < 2 || !this.grid || !this.dataset.previous) return;
      this.updateCount(this.i - 1);
      const link = document.createElement('a');
      link.className = 'button load-more load-more--previous';
      link.textContent = this.dataset.previous;
      const params = new URLSearchParams(location.search);
      const setHref = () => {
        params.set('page', this.firstPage - 1);
        link.href = location.pathname + '?' + params.toString();
      };
      setHref();
      link.addEventListener('click', (e) => {
        e.preventDefault();
        if (link.classList.contains('loading')) return;
        this.clearError();
        link.classList.add('loading');
        this.fetchPage(link.getAttribute('href'))
          .then((html) => {
            const grid = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');
            this.grid.prepend(...grid.querySelectorAll('.column'));
            this.convertPrices();
            this.firstPage--;
            this.updateCount(this.i - 1);
            if (this.firstPage < 2) link.remove();
            else setHref();
          })
          .catch(() => this.showError(link))
          .finally(() => link.classList.remove('loading'));
      });
      this.grid.before(link);
    }
    updateCount(page) {
      const total = parseInt(this.dataset.total, 10) || 0,
        shown = Math.min((page - this.firstPage + 1) * (parseInt(this.dataset.pageSize, 10) || 0), total),
        count = this.querySelector('.load-more__count'),
        bar = this.querySelector('.load-more__bar i');
      if (count && count.dataset.countTemplate) count.textContent = count.dataset.countTemplate.replace('__N__', shown);
      if (bar && total) bar.style.width = (shown / total * 100) + '%';
    }
    // After page `page` is in: count, bar, the link's next page, the address bar, and the button at the end.
    updateProgress(page) {
      this.updateCount(page);
      const params = new URLSearchParams(location.search);
      params.set('page', page);
      // So "back" from a product returns to the page it was on, not the first one.
      history.replaceState(history.state, '', location.pathname + '?' + params.toString());
      if (this.button) {
        if (this.pages && page >= this.pages) {
          this.button.remove();
          this.button = null;
        } else {
          params.set('page', page + 1);
          this.button.href = location.pathname + '?' + params.toString();
        }
      }
    }
    renderProducts(html, url) {
      let base = this,
        grid_to_replace = new DOMParser().parseFromString(html, 'text/html').getElementById('product-grid');

      if (!grid_to_replace) {
        if (base.observer) {
          base.observer.unobserve(base);
        }
        base.removeAttribute('loading');
        if (base.button) {
          base.button.classList.add('visually-hidden');
        }
        return;
      }
      base.updateProgress(base.i - 1);
      let products = grid_to_replace.querySelectorAll('.column');

      for (var i = 0; i < products.length; i++) {
        this.grid.appendChild(products[i]);
      }
      if (this.animations_enabled) {
        gsap.set(products, {
          opacity: 0,
          y: 30
        });
        gsap.to(products, {
          duration: 0.5,
          y: 0,
          opacity: 1,
          stagger: 0.05,
          onComplete: function () {
            base.removeAttribute('loading');
          }
        });
      } else {
        base.removeAttribute('loading');
      }
      base.convertPrices();
    }
  }
  customElements.define('pagination-theme', Pagination);
}